import React, { useState, useEffect } from 'react';
import { Table, Pagination, Form, InputGroup, Button, Badge, Spinner } from 'react-bootstrap';
import DataService from '../../services/data.service';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [currentPage, perPage, search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await DataService.getUsers(currentPage, perPage, search);

      if (response.data.success) {
        setUsers(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalUsers(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setCurrentPage(1);
  };

  const handlePerPageChange = (e) => {
    setPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => setCurrentPage(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} />
      );
    }

    // Page numbers
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => setCurrentPage(totalPages)} />
      );
    }

    return items;
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col-md-12">
          <h2 className="mb-3">Users Statistics</h2>

          {/* Search and Filter Controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Form onSubmit={handleSearch} className="d-flex">
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ minWidth: '300px' }}
                />
                <Button variant="primary" type="submit">
                  <i className="bi bi-search"></i> Search
                </Button>
                {search && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSearch('');
                      setSearchInput('');
                      setCurrentPage(1);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Form>

            <div className="d-flex align-items-center gap-2">
              <span>Show</span>
              <Form.Select
                value={perPage}
                onChange={handlePerPageChange}
                style={{ width: 'auto' }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
              <span>entries</span>
            </div>
          </div>

          {/* Total Count */}
          <p className="text-muted">
            Showing {users.length > 0 ? ((currentPage - 1) * perPage + 1) : 0} to{' '}
            {Math.min(currentPage * perPage, totalUsers)} of {totalUsers} users
          </p>

          {/* Users Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="alert alert-info text-center">
              No users found{search ? ` matching "${search}"` : ''}.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th className="text-center">Completed</th>
                      <th className="text-center">Cancelled</th>
                      <th className="text-center">Open</th>
                      <th className="text-center">Confirmed</th>
                      <th className="text-center">Total</th>
                      <th className="text-end">Revenue</th>
                      <th>Last Booking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.email}>
                        <td>{(currentPage - 1) * perPage + index + 1}</td>
                        <td>
                          <strong>{user.name}</strong>
                        </td>
                        <td>
                          <a href={`mailto:${user.email}`}>{user.email}</a>
                        </td>
                        <td>
                          <a href={`tel:${user.phone}`}>{user.phone}</a>
                        </td>
                        <td className="text-center">
                          <Badge bg="success">{user.completed_bookings}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="danger">{user.cancelled_bookings}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="warning" text="dark">{user.open_bookings}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="info">{user.confirmed_bookings}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="primary">{user.total_bookings}</Badge>
                        </td>
                        <td className="text-end">
                          <strong>${user.total_revenue.toFixed(2)}</strong>
                        </td>
                        <td>{formatDate(user.last_booking_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>{renderPagination()}</Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
